import { Button, Nav, Offcanvas } from "react-bootstrap"
import { Outlet, Link, useLocation, matchPath } from "react-router-dom"
import { Bell, Component, LogOut, Pencil, User, UserPen } from "lucide-react"
import { useState } from "react"

import useAuth from "../../hooks/useAuth"
import { useFetchUserDataQuery } from "../../app/userApiSlice"
import { useLogoutUserMutation } from "../../app/authApiSlice"
import SkeletonBody from "../skeletonLayout/SkeletonBody"
import Footer from "../../components/Footer"
import StatusMessagePage from "../../features/user/pages/StatusMessagePage"
import Header from "../../components/Header"
import isMembershipExpired from "../../utils/membership/isMembershipExpired"

const UserLayout = () => {
  const { pathname } = useLocation()
  const { uid, emailVerified } = useAuth()
  const [logoutUser] = useLogoutUserMutation()
  const {
    data,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useFetchUserDataQuery(uid)

  const [showMenu, setShowMenu] = useState(false)
  const handleToggleMenu = () => setShowMenu(!showMenu)

  const showCurrentPage = (path) => pathname.startsWith(path) ? "text-primary" : "text-muted custom-hover"

  const onLogoutClicked = () => {
    logoutUser()
  }

  if (isLoading) return <SkeletonBody />

  if (isError) {
    console.error(error || "An error occurred")
    return <StatusMessagePage status="failedToLoadData" />
  }

  if (!isSuccess || !data) return null

  // Skip email verification only in development as i kept forgetting to uncomment it!
  if (!import.meta.env.DEV && !emailVerified) {
    return <StatusMessagePage status="verifyEmail" />
  }

  if (!data.memberDetailsComplete) return <StatusMessagePage status="signup" />

  if (data.disabled) return <StatusMessagePage status="disabled" />

  if (!data.membership?.initialPaymentComplete) return <StatusMessagePage status="paymentIncomplete" />

  if (isMembershipExpired(data.membership?.validUntil)) return <StatusMessagePage status="expired" />

  const { notifications } = data

  return (
    <>
      <Header handleToggleMenu={handleToggleMenu} showMenu={showMenu} />

      {/* Sidebar */}
      <Offcanvas
        className="d-flex flex-column pt-5 px-2 px-sm-3 border-end position-fixed bg-white no-scrollbar off-canvas"
        style={{ width: "260px", top: "4.8375rem", bottom: "0", zIndex: "1042" }}
        show={showMenu}
        onHide={handleToggleMenu}
        responsive="lg"
      >

        <h4 className="mb-4 fw-bold px-4">Menu</h4>
        <Nav className="flex-grow-1 d-flex flex-column">
          <Link
            to="/user"
            className={`d-flex align-items-center px-4 mb-2 fs-6 rounded text-decoration-none py-2 fw-semibold ${pathname === "/user" ? "text-primary" : "text-muted custom-hover"}`}
            onClick={handleToggleMenu}
          >
            <User className="me-2" strokeWidth={1.75} />
            Profile
          </Link>
          {data.clubs.length > 0 && (
            <Link
              to={`/user/clubs`}
              className={`d-flex align-items-center px-4 mb-2 fs-6 rounded text-decoration-none py-2 fw-semibold ${showCurrentPage("/user/clubs")}`}
              onClick={handleToggleMenu}
            >
              <Component className="me-2" strokeWidth={1.75} />
              Clubs
            </Link>
          )}
          <Link
            to="/user/update-details"
            className={`d-flex align-items-center px-4 mb-2 fs-6 rounded text-decoration-none py-2 fw-semibold ${showCurrentPage("/user/update-details")}`}
            onClick={handleToggleMenu}
          >
            <UserPen className="me-2" strokeWidth={1.75} />
            Update Profile
          </Link>
          <Link
            to="/user/change-email"
            className={`d-flex align-items-center px-4 mb-2 fs-6 rounded text-decoration-none py-2 fw-semibold ${showCurrentPage("/user/change-email")}`}
            onClick={handleToggleMenu}
          >
            <Pencil className="me-2" strokeWidth={1.75} />
            Change Email
          </Link>
          <Link
            to="/user/notifications"
            className={`px-4 mb-2 rounded text-decoration-none fw-semibold ${showCurrentPage("/user/notifications")}`}
            onClick={handleToggleMenu}
          >
            <div className="d-flex align-items-center">
              <div className="position-relative py-2">
                <Bell className="me-2" strokeWidth={1.75} />
                {notifications > 0 && <div className="notifications">{notifications}</div>}
              </div>
              <span className="d-inline fs-6">Notifications</span>
            </div>
          </Link>
          <hr className="text-muted mx-2" />
          <Button variant="link" className="d-flex align-items-center text-decoration-none px-4 my-2 rounded text-muted fw-semibold text-start custom-hover" onClick={onLogoutClicked} >
            <LogOut className="me-2" strokeWidth={1.75} />
            Logout
          </Button>
          <Link
            to="https://www.gymnasticbritannia.co.uk/privacy"
            className="d-flex align-items-center px-4 mt-auto mb-4 rounded text-decoration-none text-dark pt-2 opacity-50 fs-8"
          >
            Privacy Policy
          </Link>
        </Nav>
      </Offcanvas>

      {/* Main Content */}
      <div className="d-flex flex-column flex-grow-1 position-relative main-layout mw-100">
        <div className="h-100 d-flex flex-column position-relative">
          <div className=" py-5 px-3  p-sm-5 d-flex flex-column align-items-center h-100">
            <Outlet />
          </div>
          <Footer />
        </div>
      </div>
    </>
  )
}

export default UserLayout